import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Minus, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle, DialogClose } from "@/components/ui/dialog";
import { useCart } from "@/context/CartContext";
import { imgUrl, rupiah } from "@/lib/api";

const navBtn = "absolute top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/90 text-ink grid place-items-center hover:bg-white shadow active:scale-95 transition-transform";

const qtyBtn = "w-9 h-9 rounded-full bg-paper text-forest grid place-items-center active:scale-90 transition-transform";

const QtyControl = ({ item }) => {
  const { add, items, setQty } = useCart();
  const qty = items.find((i) => i.id === item.id)?.qty || 0;
  if (!qty) {
    return (
      <button onClick={() => { add(item); toast.success(`${item.name} ditambahkan`, { duration: 1200 }); }} className="shrink-0 bg-paper text-forest font-semibold text-sm px-4 py-2 rounded-full flex items-center gap-1.5 active:scale-95 transition-transform" data-testid="menu-image-viewer-add">
        <Plus className="w-4 h-4" /> Tambah
      </button>
    );
  }
  return (
    <div className="shrink-0 flex items-center gap-2" data-testid="menu-image-viewer-qty">
      <button onClick={() => setQty(item.id, qty - 1)} className={qtyBtn} aria-label="Kurangi" data-testid="menu-image-viewer-qty-minus"><Minus className="w-4 h-4" /></button>
      <span className="w-6 text-center font-bold text-lg tabular-nums" data-testid="menu-image-viewer-qty-value">{qty}</span>
      <button onClick={() => add(item)} className={qtyBtn} aria-label="Tambah" data-testid="menu-image-viewer-qty-plus"><Plus className="w-4 h-4" /></button>
    </div>
  );
};

const PAIRS = { kopi: ["makanan", "camilan"], nonkopi: ["makanan", "camilan"], makanan: ["kopi", "nonkopi"], camilan: ["kopi", "nonkopi"], paket: ["camilan", "nonkopi", "kopi"] };

const suggestFor = (item, menu) => {
  const cats = PAIRS[item.category] || [];
  return menu
    .filter((m) => m.id !== item.id && cats.includes(m.category))
    .sort((a, b) => (a.rank || 99) - (b.rank || 99) || cats.indexOf(a.category) - cats.indexOf(b.category) || a.price - b.price)
    .slice(0, 3);
};

const Suggestions = ({ item, menu }) => {
  const { add } = useCart();
  const list = suggestFor(item, menu || []);
  if (!list.length) return null;
  return (
    <div className="px-5 pb-5" data-testid="menu-image-viewer-suggestions">
      <div className="text-xs uppercase tracking-wider text-paper/70 font-semibold mb-2">Cocok dipesan bersama</div>
      <div className="grid grid-cols-3 gap-2">
        {list.map((m) => (
          <div key={m.id} className="bg-white/10 rounded-xl overflow-hidden text-paper flex flex-col" data-testid={`menu-suggestion-${m.id}`}>
            <img src={imgUrl(m.image)} alt={m.name} className="w-full aspect-[4/3] object-cover" loading="lazy" />
            <div className="p-2 flex-1 flex flex-col">
              <div className="text-xs font-semibold leading-tight line-clamp-2">{m.name}</div>
              <div className="flex items-center justify-between gap-1 mt-auto pt-1.5">
                <span className="text-[11px] font-bold">{rupiah(m.price)}</span>
                <button onClick={() => { add(m); toast.success(`${m.name} ditambahkan`, { duration: 1200 }); }} className="w-7 h-7 rounded-full bg-paper text-forest grid place-items-center active:scale-90 transition-transform" aria-label={`Tambah ${m.name}`} data-testid={`menu-suggestion-add-${m.id}`}><Plus className="w-4 h-4" /></button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const NoteField = ({ item }) => {
  const { items, setItemNote } = useCart();
  const line = items.find((i) => i.id === item.id);
  if (!line) return null;
  return (
    <div className="px-5 pb-4">
      <input value={line.note || ""} onChange={(e) => setItemNote(item.id, e.target.value)} maxLength={100}
        placeholder="Catatan: less sugar, tanpa es..."
        className="w-full px-3 py-2.5 rounded-lg bg-white/10 border border-white/20 text-paper placeholder:text-paper/50 text-sm focus:outline-none focus:ring-2 focus:ring-paper/40"
        data-testid="menu-image-viewer-note-input" />
    </div>
  );
};

export const MenuImageViewer = ({ items, startId, open, onOpenChange, menu }) => {
  const [idx, setIdx] = useState(0);
  const touch = useRef(null);
  useEffect(() => {
    if (open) setIdx(Math.max(0, items.findIndex((m) => m.id === startId)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, startId]);
  const item = items[idx];
  const many = items.length > 1;
  const step = (d) => setIdx((i) => (i + d + items.length) % items.length);
  const onKey = (e) => {
    if (!many) return;
    if (e.key === "ArrowLeft") step(-1);
    if (e.key === "ArrowRight") step(1);
  };
  const onTouchStart = (e) => { touch.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }; };
  const onTouchEnd = (e) => {
    if (!many || !touch.current) return;
    const dx = e.changedTouches[0].clientX - touch.current.x;
    const dy = e.changedTouches[0].clientY - touch.current.y;
    touch.current = null;
    if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) step(dx < 0 ? 1 : -1);
  };
  if (!item) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent onKeyDown={onKey} className="max-w-3xl w-[95vw] max-h-[92vh] overflow-y-auto p-0 bg-ink border-0 [&>button]:hidden" data-testid="menu-image-viewer">
        <DialogTitle className="sr-only">{item.name}</DialogTitle>
        <div className="sticky top-0 h-0 z-20">
          <div className="absolute top-3 right-3">
            <DialogClose className="w-10 h-10 rounded-full bg-white/90 text-ink grid place-items-center hover:bg-white shadow" data-testid="menu-image-viewer-close" aria-label="Tutup">
              <X className="w-5 h-5" />
            </DialogClose>
          </div>
        </div>
        <div className="relative touch-pan-y select-none" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd} data-testid="menu-image-viewer-swipe-area">
          <img src={imgUrl(item.image)} alt={item.name} draggable={false} className="w-full max-h-[45vh] object-contain bg-ink" data-testid="menu-image-viewer-image" />
          {many && (
            <>
              <button type="button" onClick={() => step(-1)} className={`${navBtn} left-3`} aria-label="Menu sebelumnya" data-testid="menu-image-viewer-prev"><ChevronLeft className="w-6 h-6" /></button>
              <button type="button" onClick={() => step(1)} className={`${navBtn} right-3`} aria-label="Menu berikutnya" data-testid="menu-image-viewer-next"><ChevronRight className="w-6 h-6" /></button>
            </>
          )}
        </div>
        <div className="flex items-center justify-between gap-4 px-5 py-4 text-paper">
          <div className="min-w-0">
            <div className="font-semibold text-lg" data-testid="menu-image-viewer-name">{item.name}</div>
            {item.description && <div className="text-sm opacity-80">{item.description}</div>}
            <div className="font-bold mt-1">{rupiah(item.price)}{many && <span className="font-normal text-xs opacity-70 ml-2" data-testid="menu-image-viewer-counter">{idx + 1} / {items.length}</span>}</div>
          </div>
          <QtyControl item={item} />
        </div>
        <NoteField item={item} />
        <Suggestions item={item} menu={menu} />
      </DialogContent>
    </Dialog>
  );
};
